import { z } from 'zod';
import { DeviceSyncResponseSchema } from '@am3nd-org/pointcraft-types';

export type OperatorAuthenticationSettings = {
  requireOperatorAuthentication: boolean;
  operatorAuthenticationDurationMinutes: number;
};

const DEFAULT_OPERATOR_AUTHENTICATION_SETTINGS: OperatorAuthenticationSettings = {
  requireOperatorAuthentication: false,
  operatorAuthenticationDurationMinutes: 15,
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

const OperatorAuthenticationSettingsSchema = z.object({
  requireOperatorAuthentication: z.boolean().default(false),
  operatorAuthenticationDurationMinutes: z.number().int().nonnegative().default(15),
});

const DeviceSyncOperatorSettingsOverlaySchema = z.object({
  businessData: z
    .object({
      access_v2: z.object({
        businessAccess: z.object({
          settings: z
            .object({
              system: OperatorAuthenticationSettingsSchema.optional(),
            })
            .optional(),
        }),
      }),
    })
    .optional(),
});

type DeviceSyncCompatibilityResponse = z.infer<typeof DeviceSyncResponseSchema> &
  z.infer<typeof DeviceSyncOperatorSettingsOverlaySchema>;

type FutureEnumValue = {
  path: Array<string | number>;
  received: string;
  canonicalPlaceholder: string;
};

function replaceValueAtPath(
  value: unknown,
  path: readonly (string | number)[],
  replacement: unknown
): unknown {
  if (!path.length) return replacement;

  const [segment, ...remainingPath] = path;
  if (typeof segment === 'number') {
    if (!Array.isArray(value) || segment < 0 || segment >= value.length) return value;

    const updated = [...value];
    updated[segment] = replaceValueAtPath(updated[segment], remainingPath, replacement);
    return updated;
  }

  const record = asRecord(value);
  if (!record || !Object.prototype.hasOwnProperty.call(record, segment)) return value;

  return {
    ...record,
    [segment]: replaceValueAtPath(record[segment], remainingPath, replacement),
  };
}

function readFutureEnumValues(error: z.ZodError): FutureEnumValue[] {
  return error.issues.flatMap((issue) => {
    if (issue.code !== z.ZodIssueCode.invalid_enum_value || typeof issue.received !== 'string') {
      return [];
    }

    const canonicalPlaceholder = issue.options.find(
      (option): option is string => typeof option === 'string'
    );
    if (!canonicalPlaceholder) return [];

    return [{ path: issue.path, received: issue.received, canonicalPlaceholder }];
  });
}

/**
 * The server owns Device Sync vocabularies and can add string enum values before
 * an installed client updates its shared-types package. Substitute known values
 * only long enough to validate the rest of the payload, then restore the server
 * values. Structural errors and non-string enum mismatches still fail normally.
 */
function safeParseCanonicalDeviceSyncResponse(value: unknown) {
  const initial = DeviceSyncResponseSchema.safeParse(value);
  if (initial.success) return initial;

  const futureEnumValues = readFutureEnumValues(initial.error);
  if (!futureEnumValues.length) return initial;

  const compatibleCandidate = futureEnumValues.reduce(
    (candidate, enumValue) =>
      replaceValueAtPath(candidate, enumValue.path, enumValue.canonicalPlaceholder),
    value
  );
  const compatible = DeviceSyncResponseSchema.safeParse(compatibleCandidate);
  if (!compatible.success) return compatible;

  const restored = futureEnumValues.reduce(
    (candidate, enumValue) => replaceValueAtPath(candidate, enumValue.path, enumValue.received),
    compatible.data as unknown
  );

  return {
    success: true as const,
    data: restored as z.infer<typeof DeviceSyncResponseSchema>,
  };
}

function replaceManualDurationForCanonicalValidation(value: unknown): unknown {
  const root = asRecord(value);
  const businessData = asRecord(root?.businessData);
  const access = asRecord(businessData?.access_v2);
  const businessAccess = asRecord(access?.businessAccess);
  const settings = asRecord(businessAccess?.settings);
  const system = asRecord(settings?.system);
  if (system?.operatorAuthenticationDurationMinutes !== 0) return value;

  return {
    ...root,
    businessData: {
      ...businessData,
      access_v2: {
        ...access,
        businessAccess: {
          ...businessAccess,
          settings: {
            ...settings,
            system: { ...system, operatorAuthenticationDurationMinutes: 1 },
          },
        },
      },
    },
  };
}

/**
 * Shared-types 8.5.2 still requires a positive duration. Validate the complete
 * canonical Device Sync payload against that schema with a temporary positive
 * value, then merge the independently validated operator settings back into
 * the parsed result. This compatibility path can be removed after the locked
 * shared-types release accepts zero as manual mode.
 */
export const DeviceSyncResponseCompatibilitySchema = z.unknown().transform((value, context) => {
  const canonical = safeParseCanonicalDeviceSyncResponse(
    replaceManualDurationForCanonicalValidation(value)
  );
  const overlay = DeviceSyncOperatorSettingsOverlaySchema.safeParse(value);

  if (!canonical.success) {
    canonical.error.issues.forEach((issue) => context.addIssue(issue));
    return z.NEVER;
  }
  if (!overlay.success) {
    overlay.error.issues.forEach((issue) => context.addIssue(issue));
    return z.NEVER;
  }

  const operatorSystem = overlay.data.businessData?.access_v2.businessAccess.settings?.system;
  if (!operatorSystem || !canonical.data.businessData) {
    return canonical.data as DeviceSyncCompatibilityResponse;
  }

  const canonicalBusiness = canonical.data.businessData;
  return {
    ...canonical.data,
    businessData: {
      ...canonicalBusiness,
      access_v2: {
        ...canonicalBusiness.access_v2,
        businessAccess: {
          ...canonicalBusiness.access_v2.businessAccess,
          settings: {
            ...canonicalBusiness.access_v2.businessAccess.settings,
            system: {
              ...canonicalBusiness.access_v2.businessAccess.settings.system,
              ...operatorSystem,
            },
          },
        },
      },
    },
  } as DeviceSyncCompatibilityResponse;
});

function readSystemSettings(businessData: unknown): Record<string, unknown> | null {
  const business = asRecord(businessData);
  const access = asRecord(business?.access_v2);
  const businessAccess = asRecord(access?.businessAccess);
  const settings = asRecord(businessAccess?.settings);
  return asRecord(settings?.system);
}

export function getOperatorAuthenticationSettings(
  businessData: unknown
): OperatorAuthenticationSettings {
  const system = readSystemSettings(businessData);
  const duration = system?.operatorAuthenticationDurationMinutes;

  return {
    requireOperatorAuthentication: system?.requireOperatorAuthentication === true,
    operatorAuthenticationDurationMinutes:
      typeof duration === 'number' && Number.isInteger(duration) && duration >= 0
        ? duration
        : DEFAULT_OPERATOR_AUTHENTICATION_SETTINGS.operatorAuthenticationDurationMinutes,
  };
}

export function isOperatorAuthenticationRequired(businessData: unknown): boolean {
  return getOperatorAuthenticationSettings(businessData).requireOperatorAuthentication;
}
